import React, { memo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory, useLocation } from 'react-router-dom';
import KeepAlive, { useActivate, useUnactivate } from 'react-activation';
import styles from './WpTurnBtn.module.less';

import { logging, logPostInfo } from '../../actions/log';
import {
  updateShowWpTurnStart,
  updateShowWpTurnLimit,
  updateShowWpOrder,
  showBatchAddInfo,
} from '../../actions/wallpaper';

import Toast from '../Toast/Toast';
import { PCStoreJsBridge } from '../../nativeapi';
import config from '../../pages/Wallpaper/config';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectWallpaper = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper,
);
const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);

export default memo(({ wallpaper, cardInfo, logInfo, classType }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);
  const userInfo = useSelector(selectUserInfo);
  const {} = useSelector(selectWallpaper);

  const [isInTurn, setIsInTurn] = useState(false);
  const [isInTurnByUser, setIsInTurnByUser] = useState(false);

  useActivate(() => {
    if (getSessionStorageTurnWp(wallpaper) === 'del') {
      setIsInTurn(false);
    } else if (getSessionStorageTurnWp(wallpaper) === 'add') {
      setIsInTurn(true);
    } else {
      setIsInTurn(wallpaper?.hasCarousel || false);
    }
  });

  useEffect(() => {
    if (!userInfo?.st) {
      clearSessionStorageTurnWp();
    }
  }, [userInfo]);

  useEffect(() => {
    if (getSessionStorageTurnWp(wallpaper) === 'del') {
      setIsInTurn(false);
    } else if (getSessionStorageTurnWp(wallpaper) === 'add') {
      setIsInTurn(true);
    } else {
      if (!userInfo?.st) {
        setIsInTurn(false);
      } else {
        setIsInTurn(wallpaper?.hasCarousel || false);
      }
    }
  }, [
    wallpaper,
    userInfo,
    sessionStorage.getItem('Wp_Turn_List_Del'),
    sessionStorage.getItem('Wp_Turn_List_Add'),
  ]);

  useEffect(() => {
    if (isInTurnByUser && userInfo && userInfo.st) {
      handleWpTurn();
      setIsInTurnByUser(false);
    }
  }, [isInTurnByUser, userInfo]);

  const failToast = useCallback(content => {
    Toast.error({
      content: '操作失败，请重试！',
    });
  }, []);

  const callbackTurnStart = useCallback(
    async flag => {
      if (flag) {
        handleWpTurn('isTurn');
      }
    },
    [wallpaper, isInTurn],
  );

  const callbackTurnLimit = useCallback(
    async flag => {
      if (flag) {
        handleWpTurn('isLimit');
      }
    },
    [wallpaper, isInTurn],
  );

  const callbackTurnOrder = useCallback(async flag => {
    if (flag) {
      handleWpTurn('isOrder');
    }
  }, []);

  const handleWpTurn = useCallback(
    async flag => {
      // 判断是否登录
      if (!userInfo || !userInfo.st) {
        window.CallNativeMethod('login'); // 未登录 调用登录弹窗
        setIsInTurnByUser(true);
        return;
      }

      if (flag === 'isClick') {
        loggingFunc(
          'Wallpaper',
          isInTurn ? 'C_Wallpaper_LoopCancel_Single' : 'C_Wallpaper_Loop_Single',
        );
      }

      try {
        // 准备添加到轮播列表
        let res;
        if (isInTurn) {
          let param = { contentId: wallpaper.wallpaperId, groupType: '1' };
          res = await config.delWpToTurn({ ...param });
        } else {
          let param = {
            contentList: [{ contentId: wallpaper.wallpaperId, groupType: '1' }],
            type: '1',
          };
          res = await config.addWpToTurn({ ...param });
        }
        if (res?.status === 0) {
          if (isInTurn) {
            Toast.success({ content: '从壁纸轮播列表中删除成功' });
            delSessionStorageTurnWp(wallpaper);
          } else {
            Toast.success({ content: '已成功添加至轮播列表' });
            addSessionStorageTurnWp(wallpaper);
          }

          setIsInTurn(state => {
            return !state;
          });

          // 验证是否开始轮播
          if (window?.external?.get_wallpaper_carousel_switch_info) {
            let switchTurn = JSON.parse(
              window.external.get_wallpaper_carousel_switch_info() || '{}',
            );
            if (switchTurn?.switch !== 'on') {
              dispatch(
                updateShowWpTurnStart({
                  show: true,
                  callbackTurnStart: callbackTurnStart,
                }),
              );
            }
          }
        } else if (res?.status === 1) {
          // 1.添加超过5张
          dispatch(
            updateShowWpOrder({
              show: true,
              wallpaperId: wallpaper?.wallpaperId,
              code: '',
              wpType: 1, // 1: 单张壁纸，2：壁纸包 3: 仅仅订阅
              wallpaper: wallpaper,
              cardInfo: cardInfo,
              logInfo: logInfo,
              callbackTurnOrder: callbackTurnOrder,
              scenetype: 'loop_single_limit5', // 'set', 'dl', 'resident', 'loop_single', 'loop_single_limit5', 'loop_batch', 'loop_start'
            }),
          );
          return;
        } else if (res?.status === 2) {
          // 2.非订阅用户允许提交分类轮播
          dispatch(
            updateShowWpOrder({
              show: true,
              wallpaperId: wallpaper?.wallpaperId,
              code: '',
              wpType: 1, // 1: 单张壁纸，2：壁纸包 3: 仅仅订阅
              wallpaper: wallpaper,
              cardInfo: cardInfo,
              logInfo: logInfo,
              callbackTurnOrder: callbackTurnOrder,
              scenetype: 'loop_single', // 'set', 'dl', 'resident', 'loop_single', 'loop_batch', 'loop_start'
            }),
          );
          return;
        } else {
          Toast.error({
            content: isInTurn ? '取消壁纸到轮播列表失败' : '添加壁纸到轮播列表失败',
          });
        }
        loggingFunc(
          'Wallpaper',
          isInTurn ? 'R_Wallpaper_LoopCancel_Single' : 'R_Wallpaper_Loop_Single',
          { optResult: res?.status === 0 ? 1 : 0 },
        );
      } catch {}
      dispatch(showBatchAddInfo(false));
    },
    [wallpaper, isInTurn, userInfo],
  );

  const addSessionStorageTurnWp = useCallback(wp => {
    if (!wp?.wallpaperId) {
      return;
    }
    let turnList_add = JSON.parse(sessionStorage.getItem('Wp_Turn_List_Add') || '[]');
    let turnList_del = JSON.parse(sessionStorage.getItem('Wp_Turn_List_Del') || '[]');
    turnList_add = turnList_add.filter(item => item !== wp?.wallpaperId);
    turnList_del = turnList_del.filter(item => item !== wp?.wallpaperId);
    sessionStorage.setItem('Wp_Turn_List_Add', JSON.stringify([...turnList_add, wp?.wallpaperId]));
    sessionStorage.setItem('Wp_Turn_List_Del', JSON.stringify([...turnList_del]));
  }, []);
  const delSessionStorageTurnWp = useCallback(wp => {
    if (!wp?.wallpaperId) {
      return;
    }
    let turnList_add = JSON.parse(sessionStorage.getItem('Wp_Turn_List_Add') || '[]');
    let turnList_del = JSON.parse(sessionStorage.getItem('Wp_Turn_List_Del') || '[]');
    turnList_add = turnList_add.filter(item => item !== wp?.wallpaperId);
    turnList_del = turnList_del.filter(item => item !== wp?.wallpaperId);
    sessionStorage.setItem('Wp_Turn_List_Add', JSON.stringify([...turnList_add]));
    sessionStorage.setItem('Wp_Turn_List_Del', JSON.stringify([...turnList_del, wp?.wallpaperId]));
  }, []);
  const getSessionStorageTurnWp = useCallback(wp => {
    if (!wp?.wallpaperId) {
      return '';
    }
    let turnList_add = JSON.parse(sessionStorage.getItem('Wp_Turn_List_Add') || '[]');
    let turnList_del = JSON.parse(sessionStorage.getItem('Wp_Turn_List_Del') || '[]');
    let res = '';
    if (turnList_add.indexOf(wp?.wallpaperId) > -1) {
      res = 'add';
    }
    if (turnList_del.indexOf(wp?.wallpaperId) > -1) {
      res = 'del';
    }
    return res;
  }, []);
  const clearSessionStorageTurnWp = useCallback(wp => {
    sessionStorage.setItem('Wp_Turn_List_Add', '');
    sessionStorage.setItem('Wp_Turn_List_Del', '');
  }, []);

  const loggingFunc = useCallback(
    (category, action, param2 = {}) => {
      dispatch(
        logging({
          category: category || 'Wallpaper',
          action: action || 'C_Wallpaper_LoopCancel_Single',
          label: '',
          param2: {
            source: logInfo?.source || logInfoData?.source,
            source_p: logInfoData?.source_p,
            source_pp: logInfoData?.source_pp,
            wallpaperId: wallpaper?.wallpaperId,
            ...param2,
          },
        }),
      );
    },
    [wallpaper, cardInfo, logInfo],
  );

  return (
    <>
      <div
        className={`${
          classType?.type === 'wp_img' ? styles.wp_turn_btn : styles.wp_turn_btn_big
        } ${classType?.mame || ''}`}
        onClick={() => {
          handleWpTurn('isClick');
        }}
      >
        <span
          className={
            classType?.type === 'wp_img'
              ? `icon iconfont ${isInTurn ? 'icon-wp_turn_c' : 'icon-wp_turn_a'} ${
                  styles.wp_turn_icon
                }`
              : `icon iconfont ${isInTurn ? 'icon-wp_turn_c' : 'icon-wp_turn_a'} ${
                  styles.wp_turn_icon_big
                }`
          }
        ></span>
        <span
          className={classType?.type === 'wp_img' ? styles.wp_turn_text : styles.wp_turn_text_big}
        >
          {isInTurn ? '取消轮播' : '添加轮播'}
        </span>
      </div>
      {/*       
      <div
        className={`${
          classType?.type === 'wp_img'
            ? isInTurn
              ? styles.turnbtn_cancle
              : styles.turnbtn_add
            : isInTurn
            ? styles.turnbtn_cancle_big
            : styles.turnbtn_add_big
        } ${classType?.mame || ''}`}
        onClick={() => {
          handleWpTurn(isInTurn ? 2 : 1);
        }}
      ></div>
       */}
    </>
  );
});
